import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';

type ReceivePaymentsHubContextValue = {
  registerRefetch: (refetch: () => void) => void;
  refetchInvoices: () => void;
};

const ReceivePaymentsHubContext = createContext<ReceivePaymentsHubContextValue | null>(null);

export const ReceivePaymentsHubProvider = ({ children }: { children: ReactNode }) => {
  const refetchRef = useRef<(() => void) | null>(null);

  const registerRefetch = useCallback((refetch: () => void) => {
    refetchRef.current = refetch;
  }, []);

  const refetchInvoices = useCallback(() => {
    refetchRef.current?.();
  }, []);

  const value = useMemo(
    () => ({ registerRefetch, refetchInvoices }),
    [registerRefetch, refetchInvoices]
  );

  return (
    <ReceivePaymentsHubContext.Provider value={value}>
      {children}
    </ReceivePaymentsHubContext.Provider>
  );
};

export const useReceivePaymentsHub = () => {
  const context = useContext(ReceivePaymentsHubContext);
  if (!context) {
    throw new Error('useReceivePaymentsHub must be used within ReceivePaymentsHubProvider');
  }
  return context;
};

export const useReceivePaymentsHubOptional = () =>
  useContext(ReceivePaymentsHubContext);
